import { SolvableConfig, InteractiveConfig } from 'visualizations/shared/InteractiveModule';
import { isServerEnv } from 'frontend/utils/environment';

const RE_VISUALIZATION = /\[\[visualization-([a-zA-Z0-9_-]+)\]\]/g;

export type VizType =
  | 'GRAPH_2D'
  | 'GRAPH_3D'
  | 'QUANTUM_CIRCUIT'
  | 'OPERATOR_SEARCH'
  | 'HUYGENS_CLOCK'
  | 'COIN_FLIP'
  | 'CANVAS'
  | 'LOGIC_CIRCUIT'
  | 'FREEFORM_JAVASCRIPT'
  | 'GREEDY_SCHEDULER'
  | 'CS_ONE_OFFS'
  | 'DRONE_GAME'
  | 'GRID_FILL'
  | 'ORDER_LOGIC'
  | 'NUMBER_BUNCHERS'
  | 'CALCDOKU'
  | 'SHERLOCK_SEARCH'
  | 'NEURAL_NETWORKS'
  | 'PARSONS_PROBLEM'
  | 'HOT_COLD'
  | 'CRYPTO_ONE_OFFS'
  | 'TOGGLE_TAGS'
  | 'DIAGRAMMAR'
  | 'HEXAPAWN';

function getModuleForVisualizationType(visualizationType: VizType) {
  switch (visualizationType) {
    case 'GRAPH_2D':
      return import(/* webpackChunkName: 'viz-02-graph' */ 'visualizations/apps/Graph2D');
    case 'GRAPH_3D':
      return import(/* webpackChunkName: 'viz-02-escher' */ 'visualizations/apps/Graph3D');
    case 'QUANTUM_CIRCUIT':
      return import(
        /* webpackChunkName: 'viz-02-quantum-circuit' */ 'visualizations/apps/QuantumCircuit'
      );
    case 'OPERATOR_SEARCH':
      return import(
        /* webpackChunkName: 'viz-02-operator-search' */ 'visualizations/apps/OperatorSearch'
      );
    case 'HUYGENS_CLOCK':
      return import(
        /* webpackChunkName: 'viz-02-huygens-clock' */ 'visualizations/apps/HuygensClock'
      );
    case 'COIN_FLIP':
      return import(/* webpackChunkName: 'viz-02-coinflip' */ 'visualizations/apps/CoinFlip');
    case 'CANVAS':
      // @ts-expect-error
      return import(/* webpackChunkName: 'viz-02-canvas' */ 'visualizations/apps/Canvas');
    case 'LOGIC_CIRCUIT':
      return import(
        // @ts-expect-error
        /* webpackChunkName: 'viz-02-logic-circuit' */ 'visualizations/apps/LogicCircuit'
      );
    case 'FREEFORM_JAVASCRIPT':
      return import(
        // @ts-expect-error
        /* webpackChunkName: 'viz-02-javascript' */ 'visualizations/apps/FreeformJavascript'
      );
    case 'GREEDY_SCHEDULER':
      return import(
        // @ts-expect-error
        /* webpackChunkName: 'viz-02-greedy-scheduler' */ 'visualizations/apps/GreedyScheduler'
      );
    case 'CS_ONE_OFFS':
      // @ts-expect-error
      return import(/* webpackChunkName: 'viz-02-cs-one-offs' */ 'visualizations/apps/CsOneOffs');
    case 'DRONE_GAME':
      return import(/* webpackChunkName: 'viz-02-drone-game' */ 'visualizations/apps/DroneGame');
    case 'GRID_FILL':
      return import(/* webpackChunkName: 'viz-02-grid-fill' */ 'visualizations/apps/GridFill');
    case 'ORDER_LOGIC':
      return import(/* webpackChunkName: 'viz-02-order-logic' */ 'visualizations/apps/OrderLogic');
    case 'NUMBER_BUNCHERS':
      return import(
        /* webpackChunkName: 'viz-02-number-bunchers' */ 'visualizations/apps/NumberBunchers'
      );
    case 'CALCDOKU':
      return import(/* webpackChunkName: 'viz-02-calcdoku' */ 'visualizations/apps/Calcdoku');
    case 'SHERLOCK_SEARCH':
      return import(
        /* webpackChunkName: 'viz-02-sherlock-search' */ 'visualizations/apps/SherlockSearch'
      );
    case 'NEURAL_NETWORKS':
      return import(
        /* webpackChunkName: 'viz-02-neural-networks' */ 'visualizations/apps/NeuralNetworks'
      );
    case 'PARSONS_PROBLEM':
      return import(
        /* webpackChunkName: 'viz-02-parsons-problem' */ 'visualizations/apps/ParsonsProblem'
      );
    case 'HOT_COLD':
      return import(/* webpackChunkName: 'viz-02-hot-cold' */ 'visualizations/apps/HotCold');
    case 'CRYPTO_ONE_OFFS':
      return import(
        /* webpackChunkName: 'viz-02-crypto-one-offs' */ 'visualizations/apps/CryptoOneOffs'
      );
    case 'TOGGLE_TAGS':
      return import(/* webpackChunkName: 'viz-02-toggle-tags' */ 'visualizations/apps/ToggleTags');
    case 'DIAGRAMMAR':
      return import(
        /* webpackChunkName: 'viz-02-diagrammar' */ 'visualizations/apps/Diagrammar/v2Embed'
      );
    case 'HEXAPAWN':
      return import(/* webpackChunkName: 'viz-02-hexapawn' */ 'visualizations/apps/Hexapawn');
    default:
      throw new Error(`Unknown visualization type '${visualizationType}'`);
  }
}

export async function initDcVisualization(
  vizType: VizType,
  interactiveConfig: InteractiveConfig,
  solvableConfig?: SolvableConfig,
) {
  if (isServerEnv) {
    return;
  }
  let callbacks = null;
  const { default: module } = await getModuleForVisualizationType(vizType);
  if (solvableConfig) {
    try {
      callbacks = module.createInteractiveSolvable(interactiveConfig, solvableConfig);
    } catch (e) {
      callbacks = module.createInteractive(interactiveConfig);
    }
  } else {
    callbacks = module.createInteractive(interactiveConfig);
  }
  return callbacks;
}

export function parseVisualization(solution: string) {
  return solution.replace(RE_VISUALIZATION, (_, match) => {
    return `<div class="visualization-wrapper" id="${match}">
                    <div class="loading"><span class="spinner"></span></div>
                </div>`;
  });
}
